import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import React, { useState, useMemo } from 'react';
import { aggregatedClasses, convertPropToCssClassName } from '@elliemae/ds-classnames';
import { cx } from '@elliemae/ds-utilities/utils';
import { describe } from 'react-desc';
import { BUTTON_TYPE, BUTTON_SIZE, BUTTON_VARIANT } from './constants.js';
import { btnPropTypes } from './propTypes.js';
import { defaultProps } from './defaultProps.js';

var blockName = 'button';

var getContainer = function getContainer(Element) {
  return aggregatedClasses(Element)(blockName, null, function (_ref) {
    var iconOnly = _ref.iconOnly,
        variant = _ref.variant;
    return _defineProperty({
      'icon-only': iconOnly
    }, variant, variant);
  }, {
    propsToRemoveFromFinalElement: ['cx']
  });
}; // eslint-disable-next-line complexity


var DSButton = function DSButton(_ref3) {
  var innerRef = _ref3.innerRef,
      disabled = _ref3.disabled,
      buttonType = _ref3.buttonType,
      fluidWidth = _ref3.fluidWidth,
      size = _ref3.size,
      labelText = _ref3.labelText,
      icon = _ref3.icon,
      _onBlur = _ref3.onBlur,
      _onClick = _ref3.onClick,
      _onKeyPress = _ref3.onKeyPress,
      leftIcon = _ref3.leftIcon,
      tabIndex = _ref3.tabIndex,
      variant = _ref3.variant,
      intent = _ref3.intent,
      containerProps = _ref3.containerProps,
      className = _ref3.className,
      Component = _ref3.as,
      type = _ref3.type,
      rest = _objectWithoutProperties(_ref3, ["innerRef", "disabled", "buttonType", "fluidWidth", "size", "labelText", "icon", "onBlur", "onClick", "onKeyPress", "leftIcon", "tabIndex", "variant", "intent", "containerProps", "className", "as", "type"]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      pressed = _useState2[0],
      setPressed = _useState2[1];

  var Button = useMemo(function () {
    return getContainer(Component);
  }, [Component]);
  var intentBlockName = "".concat(blockName).concat(intent ? "-".concat(intent) : '');

  var _convertPropToCssClas = convertPropToCssClassName(intentBlockName),
      intentClassName = _convertPropToCssClas.cssClassName;

  return /*#__PURE__*/React.createElement(Button, _extends({
    "data-testid": containerProps['data-testid'] || 'button-wrapper'
  }, rest, containerProps, {
    "aria-disabled": rest['aria-disabled'] !== undefined ? rest['aria-disabled'] : disabled,
    "aria-label": rest['aria-label'] || labelText || 'button',
    "aria-pressed": pressed,
    className: cx(intentClassName, className),
    classProps: {
      disabled: disabled,
      // DON'T CHANGE FOR defaultProps
      // using OR to keep ButtonGroup behavior at packages/ds-basic/src/ButtonGroup/DSButtonGroup
      buttonType: buttonType || BUTTON_TYPE.PRIMARY,
      // using or to keep ButtonGroup behavior at packages/ds-basic/src/ButtonGroup/DSButtonGroup
      size: size || BUTTON_SIZE.M,
      fluidWidth: fluidWidth,
      iconOnly: !labelText && (icon || leftIcon),
      variant: disabled ? BUTTON_VARIANT.DISABLED : variant
    },
    disabled: disabled // https://jira.elliemae.io/browse/PUI-1215
    ,
    innerRef: innerRef,
    onBlur: function onBlur(e) {
      if (disabled) return;
      setPressed(false);

      _onBlur(e);
    },
    onClick: function onClick(e) {
      if (disabled) return;
      setPressed(true);

      _onClick(e);
    },
    onKeyPress: function onKeyPress(e) {
      if (disabled) return;
      e.preventDefault();

      if (e.key === 'Enter') {
        setPressed(true);

        _onClick(e);
      }

      _onKeyPress(e);
    },
    tabIndex: tabIndex,
    type: type
  }), leftIcon, labelText && /*#__PURE__*/React.createElement("span", {
    className: "label-wrapper ".concat(icon || leftIcon ? 'with-icon' : 'no-icon'),
    "data-testid": "button-label",
    role: "presentation"
  }, /*#__PURE__*/React.createElement("span", null, labelText)), icon);
};

DSButton.defaultProps = defaultProps;
DSButton.propTypes = btnPropTypes;
var DSButtonWithSchema = describe(DSButton).description('A button');
DSButtonWithSchema.propTypes = btnPropTypes;

export default DSButton;
export { DSButton, DSButtonWithSchema };
//# sourceMappingURL=DSButton.js.map
